import React, { useEffect, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AgGridReact as AgGridReactType } from 'ag-grid-react';
import { ColDef } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

const PAGE_SIZE = 500;
const ROW_HEIGHT = 42;
const MIN_TABLE_HEIGHT = 49 + ROW_HEIGHT + 48;

interface TableViewProps {
  rowData: any[];
  columnDefs: ColDef[];
  defaultColDef: ColDef;
  gridRef: React.RefObject<AgGridReactType>;
  quickFilterText: string;
  isRTL: boolean;
  isDark: boolean;
  language: 'fa' | 'en';
  openModalId: string | null;
  setOpenModalId: (id: string | null) => void;
  isFullscreen?: boolean;
}

const TableView: React.FC<TableViewProps> = ({
  rowData,
  columnDefs,
  defaultColDef,
  gridRef,
  quickFilterText,
  isRTL,
  isDark,
  language,
  openModalId,
  setOpenModalId,
  isFullscreen = false,
}) => {
  const gridTheme = 'ag-theme-alpine';

  const localeText = useMemo(
    () => ({
      page: language === 'fa' ? 'صفحه' : 'Page',
      to: language === 'fa' ? 'تا' : 'to',
      of: language === 'fa' ? 'از' : 'of',
      next: language === 'fa' ? 'بعدی' : 'Next',
      last: language === 'fa' ? 'آخرین' : 'Last',
      first: language === 'fa' ? 'اولین' : 'First',
      previous: language === 'fa' ? 'قبلی' : 'Previous',
      loadingOoo: language === 'fa' ? 'در حال بارگذاری...' : 'Loading...',
      noRowsToShow: language === 'fa' ? 'هیچ داده‌ای یافت نشد' : 'No rows to show',
    }),
    [language],
  );

  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setQuickFilter(quickFilterText);
    }
  }, [quickFilterText, gridRef]);

  return (
    <div
      className={`${gridTheme} ag-grid-dark-mode w-full`}
      style={{
        height: isFullscreen ? 'calc(95vh - 100px)' : 'auto',
        minHeight: isFullscreen ? undefined : MIN_TABLE_HEIGHT,
        direction: isRTL ? 'rtl' : 'ltr',
      }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <AgGridReact
        key={`grid-${isRTL ? 'rtl' : 'ltr'}-${isFullscreen ? 'fs' : 'auto'}`}
        ref={gridRef}
        rowData={rowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        domLayout={isFullscreen ? 'normal' : 'autoHeight'}
        pagination={true}
        paginationPageSize={PAGE_SIZE}
        paginationPageSizeSelector={false}
        rowHeight={ROW_HEIGHT}
        animateRows={true}
        rowSelection="multiple"
        suppressRowClickSelection={true}
        context={{ language, isDark, openModalId, setOpenModalId }}
        getRowId={(params) => params.data.keyword || `row-${params.data.id || Math.random()}`}
        quickFilterText={quickFilterText}
        localeText={localeText}
      />
    </div>
  );
};

export default TableView;
